import { useEffect, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import type { Trip } from "../types";

/**
 * The zone a trip's times should be read in.
 *
 * Pages below the trip — a segment, an activity — need it to render times the
 * way the trip lived them, but have no other reason to hold the whole trip
 * document. Undefined while loading and for a trip that carries no zone; both
 * mean "fall back to the reader's own clock", which is what the app did before
 * the field existed.
 */
export function useTripZone(tripId: string | undefined): string | undefined {
  const [zone, setZone] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!tripId) {
      setZone(undefined);
      return;
    }
    let cancelled = false;
    getDoc(doc(db, "trips", tripId))
      .then((snap) => {
        if (cancelled) return;
        setZone(snap.exists() ? ((snap.data() as Trip).timezone ?? undefined) : undefined);
      })
      .catch(() => {
        if (!cancelled) setZone(undefined);
      });
    return () => {
      cancelled = true;
    };
  }, [tripId]);

  return zone;
}
